import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import Layout from '../../../../layouts';
import Page from '../../../../components/Page';
import axios from '../../../../utils/axios';
import { BotaoExportar, Num, CABECALHO_TABELA } from '../../../../components/gps';
import { Skeleton } from '../../../../components/ui/skeleton';
import Modal, { BotaoSecundario } from '../../../../components/gps/Modal';

// Todos os modais de dispositivo no padrao novo. Os de leitura sairam primeiro,
// os de comando um por vez - o ultimo foi Perda/Roubo, que trava o aparelho.
import ModalGeolocalizacao from '../../../../components/gps/ModalGeolocalizacao';
import ModalHistoricoDispositivo from '../../../../components/gps/ModalHistoricoDispositivo';
import ModalEditarDispositivo from '../../../../components/gps/ModalEditarDispositivo';
import ModalMudarGrupoNovo from '../../../../components/gps/ModalMudarGrupo';

import ModalMudarSenha from '../../../../components/gps/ModalMudarSenha';
import ModalPerdaRoubo from '../../../../components/gps/ModalPerdaRoubo';
import NextLink from 'next/link';
import { linhasIniciais, OPCOES_LINHAS, rotuloLinhas } from '../../../../components/gps/paginacao';
import MenuAcoes from '../../../../components/gps/MenuAcoes';
import TituloTela from '../../../../components/gps/TituloTela';
import CaixaBusca from '../../../../components/gps/CaixaBusca';

/* ============================================================================
   Dispositivos - visual novo (shadcn + Tailwind), COM TODAS AS FUNCOES.

   Historico honesto: a primeira versao desta tela era somente leitura e tirou o
   menu de acoes, as colunas de consumo/sinal/telefone e o filtro "Perdido". Isso
   foi erro de escopo meu - trocar o visual nao e motivo para remover funcao. As
   sete acoes e as 24 colunas voltaram aqui.

   Contrato conferido em remoteAPI/src/app/dispositivos/dispositivos.service.ts:2950.

   TRES LIMITES DO BACKEND que a tela respeita, em vez de fingir que nao existem:

   1. ORDENACAO - o backend so honra deviceId, nome, status, createdAt e
      device_log_data; qualquer outro valor ele TROCA por deviceId em silencio
      (`colunasPermitidas[ordenar] || 'dispositivo.DEVICE_ID'`). A tela antiga
      marcava `sort: true` nas 24 colunas, ou seja, 19 cabecalhos clicaveis que
      nao ordenavam nada. Aqui so as cinco reais sao clicaveis - as outras nao
      perderam nada, elas nunca ordenaram.

   2. FILTRO - a whitelist aceita 8 campos. A tela antiga oferecia 16 no seletor,
      e os 8 fora da lista eram DESCARTADOS pelo backend: a busca voltava a lista
      inteira e parecia "nao filtrou". Aqui estao os 8 que funcionam.

   3. TETO DE LINHAS - `rows` e limitado a 1000 no servidor.

   Se algum dia a whitelist crescer, e so acrescentar aqui: as constantes
   COLUNAS_ORDENAVEIS e CAMPOS_BUSCA sao o espelho dela.
   ============================================================================ */

type Dispositivo = {
  deviceId?: string;
  nome?: string;
  status?: number | string;
  deletedAt?: string | null;
  cpf?: string;
  cr?: string;
  nome_cr?: string;
  descricao_cr?: string;
  policyName?: string;
  password?: string;
  grupoNome?: string;
  grupoCodigo?: number;
  hardwareInfo_model?: string;
  hardwareInfo_manufacturer?: string;
  hardwareInfo_brand?: string;
  hardwareInfo_serialNumber?: string;
  softwareInfo_androidVersion?: string;
  networkInfo_imei?: string;
  networkInfo_wifiMacAddress?: string;
  networkInfo_telephonyInfos_phoneNumber_1?: string;
  networkInfo_telephonyInfos_phoneNumber_2?: string;
  networkInfo_telephonyInfos_carrierName_1?: string;
  networkInfo_telephonyInfos_carrierName_2?: string;
  memoryInfo_totalRam?: number | string;
  external_storage_detected?: string;
  internal_storage_detected?: string;
  bateria?: number | string;
  wifi?: string;
  sinalTelefone?: string;
  sinal_dados_moveis?: string;
  consumo_wifi?: string;
  consumo_dados_moveis?: string;
  endereco?: string;
  versaoHub?: string;
  device_log_data?: string;
  createdAt?: string;
  updatedAt?: string;
};


// Inclui PERDIDO (2), que a primeira versao desta tela tinha esquecido.
const STATUS = [
  { valor: -1, rotulo: 'Todos' },
  { valor: 1, rotulo: 'Ativo' },
  { valor: 0, rotulo: 'Inativo' },
  { valor: 2, rotulo: 'Perdido' },
];

// Espelho da whitelist de FILTRO do backend (dispositivos.service.ts:3001).
const CAMPOS_BUSCA = [
  { valor: 'auto', rotulo: 'Automático' },
  { valor: 'nome', rotulo: 'Nome' },
  { valor: 'deviceId', rotulo: 'Device ID' },
  { valor: 'cpf', rotulo: 'CPF' },
  { valor: 'networkInfo_imei', rotulo: 'IMEI' },
  { valor: 'hardwareInfo_serialNumber', rotulo: 'Serial' },
  { valor: 'softwareInfo_androidVersion', rotulo: 'Versão Android' },
  { valor: 'policyName', rotulo: 'Política' },
  { valor: 'name', rotulo: 'Name (Google)' },
];

// Espelho da whitelist de ORDENACAO do backend (dispositivos.service.ts:3041).
const COLUNAS_ORDENAVEIS = ['deviceId', 'nome', 'status', 'createdAt', 'device_log_data'];

function numero(valor: unknown) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

/** Mesma leitura de status da tela antiga, incluindo PERDIDO e PENDENTE. */
function rotuloStatus(d: Dispositivo) {
  if (d?.deletedAt) return { rotulo: 'INATIVO', classe: 'bg-danger-soft text-danger' };
  const s = String(d?.status ?? '').toLowerCase();
  if (s === '1' || s === 'ativo') return { rotulo: 'ATIVO', classe: 'bg-ok-soft text-ok' };
  if (s === '2' || s === 'perdido') return { rotulo: 'PERDIDO', classe: 'bg-warn-soft text-warn' };
  if (s === '3' || s === 'pendente') return { rotulo: 'PENDENTE', classe: 'bg-info-soft text-info' };
  return { rotulo: 'INATIVO', classe: 'bg-danger-soft text-danger' };
}

/**
 * Ultimo contato em linguagem de plantao: o que importa nao e a data, e ha quanto
 * tempo. Cor por faixa, porque a decisao ("preciso agir?") depende da faixa.
 */
function ultimoContato(valor?: string) {
  if (!valor) return { texto: 'nunca', classe: 'text-danger', titulo: 'Sem nenhum log recebido' };
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return { texto: '—', classe: 'text-faint', titulo: '' };
  const horas = (Date.now() - data.getTime()) / 36e5;
  const titulo = data.toLocaleString('pt-BR');
  if (horas < 1) return { texto: 'agora', classe: 'text-ok', titulo };
  if (horas < 24) return { texto: `${Math.floor(horas)} h`, classe: 'text-ok', titulo };
  const dias = Math.floor(horas / 24);
  if (dias <= 3) return { texto: `${dias} d`, classe: 'text-muted-foreground', titulo };
  return { texto: `${dias} d`, classe: 'font-bold text-danger', titulo };
}

/**
 * Le booleano de campo que chega do LOG do aparelho, onde ele vem como texto.
 *
 * `Boolean('false')` e `true` em JavaScript - a conversao ingenua diria "Sim" para
 * todos os aparelhos. Devolve `null` para ausente, que e diferente de `false`: um
 * aparelho que nunca reportou nao e um aparelho sem o recurso, e mostrar "Não" nos
 * dois casos afirma algo que ninguem sabe.
 */
function booleanoDoLog(v: unknown): boolean | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return v;
  const s = String(v).trim().toLowerCase();
  if (s === 'true' || s === '1' || s === 'sim') return true;
  if (s === 'false' || s === '0' || s === 'nao' || s === 'não') return false;
  return null;
}

function texto(v: unknown) {
  const s = v === null || v === undefined || v === '' ? '' : String(v);
  return s || '—';
}

type Coluna = {
  id: string;
  rotulo: string;
  largura: string;
  /** `true` = o backend realmente ordena por esta coluna. */
  ordenavel?: boolean;
  mono?: boolean;
  render: (d: Dispositivo) => React.ReactNode;
  /**
   * Valor da celula na planilha. Opcional: sem ela, vale `d[id]` - e o `id` de 22
   * das 24 colunas E o nome do campo. So as duas que a tela CALCULA (status e a
   * ultima sincronizacao) precisam declarar.
   *
   * Nao da para reaproveitar `render`: ele devolve JSX, e planilha quer texto.
   */
  exportar?: (d: Dispositivo) => string | number | null | undefined;
};

Dispositivos.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function Dispositivos() {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // `id` e o codigo do GRUPO; -1 = todos. Vem da rota, igual a tela antiga.
  const id = String(router.query.id ?? '-1');

  const [linhas, setLinhas] = useState<Dispositivo[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);

  /* QUANTAS LINHAS POR PAGINA - ESCOLHA DA PESSOA (10/09/2026, a pedido).

     Era uma constante de modulo (`linhasIniciais()`), calculada UMA vez na
     carga do arquivo: quem abrisse em 1366x768 ficava presa em 5 linhas para
     sempre, mesmo maximizando a janela depois. `linhasIniciais()` continua
     dando o valor de PARTIDA - a altura da tela e um bom palpite inicial -,
     mas agora e so o palpite. */
  const [linhasPorPagina, setLinhasPorPagina] = useState(linhasIniciais());
  const [busca, setBusca] = useState('');
  const [campoBusca, setCampoBusca] = useState('auto');
  /* ATIVOS por padrao, e nao "Todos" - pedido de 25/08/2026. O 1 e o valor de
     Ativo na lista STATUS acima; -1 e "Todos". Aparelho inativo ou perdido e
     historico, e comecar por ele obrigava a filtrar toda vez. */
  const [status, setStatus] = useState(1);
  const [ordenar, setOrdenar] = useState('device_log_data');
  const [direcao, setDirecao] = useState<'asc' | 'desc'>('desc');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [indices, setIndices] = useState({ total: 0, ativos: 0, inativos: 0, perdidos: 0 });

  // Colunas escondidas. Comeca VAZIO - todas visiveis, como na tela antiga.
  const [ocultas, setOcultas] = useState<string[]>([]);
  const [seletorAberto, setSeletorAberto] = useState(false);

  // Menu de acoes e modais
  const [menuAberto, setMenuAberto] = useState<string | null>(null);
  const [selecionado, setSelecionado] = useState<Dispositivo | null>(null);
  const [openCR, setOpenCR] = useState(false);
  const [openGrafico, setOpenGrafico] = useState(false);
  const [openSenha, setOpenSenha] = useState(false);
  const [openHistorico, setOpenHistorico] = useState(false);
  const [openGrupo, setOpenGrupo] = useState(false);
  const [openPerda, setOpenPerda] = useState(false);
  const [apagando, setApagando] = useState<Dispositivo | null>(null);
  const [removendo, setRemovendo] = useState(false);


  // Perfil 0 nao tem acoes, igual a tela antiga (`user.perfil > 0`). Esconder e
  // conveniencia; o backend tambem valida.
  const podeAgir = Number(user?.perfil ?? 0) > 0;

  const carregar = useCallback(async () => {
    if (!router.isReady) return;
    setCarregando(true);
    setErro('');
    try {
      // No modo automatico o campo sai do FORMATO do termo, dentro da whitelist:
      // 15 digitos = IMEI, so digito = CPF, com hifen = DEVICE_ID, resto = nome.
      const termo = busca.trim();
      const soDigitos = termo.replace(/\D/g, '');
      let campo = campoBusca;
      let valor = termo;

      /* "AUTOMATICO" MANDAVA DEVICE ID COMO NOME (29/09/2026, relatado).

         A regra antiga so reconhecia Device ID quando o termo tinha HIFEN. Mas
         o Device ID do Google e hexadecimal sem hifen - `32631aad2f213cb8` -,
         e nenhum dos aparelhos deste parque tem. O caminho era:

           soDigitos = '3263122138' (10) -> nao e IMEI (15)
           nao bate com o termo -> nao e CPF
           sem hifen -> nao e deviceId
           => cai em `nome`

         Buscar Device ID no campo de nome nao acha nada, e a tela nao dizia por
         que: o seletor marcava "Automatico" e o resultado vinha vazio.

         AGORA CADA FORMATO TEM UMA FORMA, e nao uma sobra:

           15 digitos exatos ............ IMEI
           so digitos e pontuacao, com
             11 ou 14 digitos ........... CPF ou CNPJ
           hexadecimal de 12 a 20, ou
             qualquer coisa com hifen ... Device ID
           maiuscula e digito, 8+ ....... numero de serie (ex.: R9QX503EXEY)
           resto ........................ nome

         A ordem importa: IMEI antes de CPF (15 digitos passaria pelos dois) e
         hexadecimal antes de serie (um Device ID so com digitos e letras a-f
         tambem casa com o padrao de serie). */
      if (campoBusca === 'auto') {
        if (/^\d{15}$/.test(termo)) {
          campo = 'networkInfo_imei';
        } else if (/^[\d.\-\s]+$/.test(termo) && (soDigitos.length === 11 || soDigitos.length === 14)) {
          campo = 'cpf';
          valor = soDigitos;
        } else if (/^[0-9a-f]{12,20}$/i.test(termo) || termo.includes('-')) {
          campo = 'deviceId';
        } else if (/^[A-Z0-9]{8,}$/.test(termo)) {
          campo = 'hardwareInfo_serialNumber';
        } else {
          campo = 'nome';
        }
      }

      const resposta = await axios.get<{ content: Dispositivo[]; totalElements: number }>(
        `/dispositivos/paginado?page=${pagina}&rows=${linhasPorPagina}` +
          `&valor=${encodeURIComponent(valor)}&campo=${campo}` +
          `&direcao=${direcao}&ordenar=${ordenar}&entidade=${encodeURIComponent(id)}` +
          `&status=${status}&regional=null`
      );
      setLinhas(resposta.data?.content || []);
      setTotal(numero(resposta.data?.totalElements));
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar os dispositivos.');
      setLinhas([]);
    } finally {
      setCarregando(false);
    }
  }, [router.isReady, pagina, linhasPorPagina, busca, campoBusca, status, ordenar, direcao, id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!router.isReady) return;
    axios
      .get<any>(`/dispositivos/findByStatus/${encodeURIComponent(id)}`)
      .then((r) => {
        const d = r.data || {};

        /* 🔴 DEFEITO CORRIGIDO (26/08/2026): ATIVOS e INATIVOS vinham ZERO com o
           TOTAL certo (31.202 no relato).

           Causa: este endpoint devolve os campos no PLURAL - `ativos`, `inativos`
           e `perdidos` (dispositivos.service.ts:3647-3655). O front lia no
           SINGULAR. A consulta ANTIGA usava singular e esta comentada logo acima
           da nova, nas linhas 3636-3637: quando a consulta foi trocada, o front
           nao acompanhou. `numero(undefined)` da 0, entao o cartao mostrava zero
           com cara de resposta valida, e nada acusava.

           Le as DUAS grafias de proposito. Dos sete endpoints de indice do
           sistema, este e o unico no plural - os outros seis usam singular. Se
           algum dia esta consulta voltar ao padrao dos outros, a tela continua
           certa em vez de zerar de novo em silencio. */
        setIndices({
          total: numero(d.total),
          ativos: numero(d.ativos ?? d.ativo),
          inativos: numero(d.inativos ?? d.inativo),
          perdidos: numero(d.perdidos ?? d.perdido),
        });
      })
      .catch(() => setIndices({ total: 0, ativos: 0, inativos: 0, perdidos: 0 }));
  }, [router.isReady, id]);

  /* O "clique fora" e o Escape saiu daqui junto com o menu proprio: quem cuida
     disso agora e o `MenuAcoes`, que ja registra os dois. Manter uma segunda
     copia significaria dois lugares fechando o mesmo menu. */

  // O detalhe fica sob a rota do GRUPO da linha, nao sob o `id` da URL atual:
  // quando a listagem esta em "todos" (-1), usar o -1 quebraria o link de volta.
  function urlDetalhe(d: Dispositivo) {
    if (!d.deviceId) return '';
    const grupo = d.grupoCodigo ?? id;
    return `/dashboard/dispositivos-novo/${grupo}/item/${encodeURIComponent(d.deviceId)}`;
  }

  function irParaDetalhe(d: Dispositivo) {
    const url = urlDetalhe(d);
    if (url) router.push(url);
  }

  function abrir(acao: (v: boolean) => void, d: Dispositivo) {
    setSelecionado(d);
    setMenuAberto(null);
    acao(true);
  }

  function trocarOrdem(coluna: string) {
    if (!COLUNAS_ORDENAVEIS.includes(coluna)) return;
    if (ordenar === coluna) setDirecao((v) => (v === 'asc' ? 'desc' : 'asc'));
    else {
      setOrdenar(coluna);
      setDirecao('asc');
    }
    setPagina(0);
  }

  const apagar = async () => {
    if (!apagando?.deviceId) return;
    setRemovendo(true);
    try {
      await axios.delete(`/dispositivos/${encodeURIComponent(apagando.deviceId)}`);
      enqueueSnackbar('Dispositivo apagado com sucesso.', { variant: 'success' });
      setApagando(null);
      await carregar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Ops ocorreu um erro no sistema.', { variant: 'error' });
    } finally {
      setRemovendo(false);
    }
  };

  // -------------------------------------------------------------------------
  // AS 24 COLUNAS da tela antiga, na mesma ordem. Nenhuma foi retirada.
  // -------------------------------------------------------------------------
  const todasColunas: Coluna[] = useMemo(
    () => [
      {
        id: 'deviceId',
        rotulo: 'Device ID',
        largura: 'minmax(210px,2fr)',
        ordenavel: true,
        // O DEVICE_ID abre o DETALHE, como na tela antiga (onViewRow ->
        // PATH_DASHBOARD.dispositivos.item). Aponta para o detalhe NOVO.
        render: (d) => (
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="min-w-0">
              {/* LINK DE VERDADE (<a href>), e nao <button> com router.push.

                  O que isso destrava, e que era impossivel antes: Ctrl+clique e
                  clique do meio abrindo o detalhe em OUTRA ABA, sem perder a
                  listagem (filtro, pagina e ordenacao). Em `<button>` o navegador
                  nao tem URL para abrir - a navegacao acontece dentro do JavaScript
                  -, entao "abrir em nova aba" simplesmente nao existia, nem pelo
                  menu do botao direito.

                  Quem trabalha na fila de suporte compara dois aparelhos lado a
                  lado; com botao, era voltar e refazer o filtro a cada troca.

                  `NextLink` (o shim para o react-router) mantem o clique NORMAL
                  como navegacao interna - sem recarregar a aplicacao. O que muda e
                  so a existencia do `href`. */}
              <NextLink
                href={urlDetalhe(d)}
                onClick={(e: any) => e.stopPropagation()}
                className="block max-w-full truncate text-[12px] font-semibold text-ink hover:text-primary hover:underline"
                title="Abrir o detalhe (Ctrl+clique abre em outra aba)"
              >
                {texto(d.deviceId)}
              </NextLink>
            </span>
          </div>
        ),
      },
      {
        /* GRUPO SAIU DE BAIXO DO DEVICE ID (10/09/2026, a pedido).

           Os dois vinham empilhados na mesma celula, e isso custava as duas
           coisas que uma tabela da: nao dava para varrer a coluna de grupos com
           o olho (cada linha alternava entre hexadecimal e nome de grupo) nem
           para exportar grupo como coluna propria. Empilhar economiza largura,
           mas aqui a tela ja rola na horizontal - a largura nao era o problema. */
        id: 'grupoNome',
        rotulo: 'Grupo',
        largura: 'minmax(140px,1.3fr)',
        render: (d) => <span className="block truncate text-[12px] text-muted-foreground">{texto(d.grupoNome)}</span>,
      },
      {
        id: 'device_log_data',
        rotulo: 'Última sincronização',
        largura: '140px',
        ordenavel: true,
        render: (d) => {
          const c = ultimoContato(d.device_log_data);
          return (
            <Num className={`text-[12px] ${c.classe}`}>
              <span title={c.titulo}>{c.texto}</span>
            </Num>
          );
        },
      },
      /* "Nome / patrimonio" e nao so "Nome": o campo guarda as duas coisas
         conforme o cliente - ora o nome de quem usa o aparelho, ora o numero de
         patrimonio. Quem abre a lista pela primeira vez nao adivinha isso, e o
         rotulo e o unico lugar onde cabe explicar. Mesmo texto usado no topo da
         tela de detalhe. */
      { id: 'nome', rotulo: 'Nome / patrimônio', largura: 'minmax(160px,1.5fr)', ordenavel: true, render: (d) => <span className="block truncate text-[12px] font-semibold">{texto(d.nome)}</span> },
      { id: 'hardwareInfo_manufacturer', rotulo: 'Marca', largura: '120px', render: (d) => <span className="block truncate text-[12px]">{texto(d.hardwareInfo_manufacturer)}</span> },
      { id: 'hardwareInfo_model', rotulo: 'Modelo', largura: '150px', render: (d) => <span className="block truncate text-[12px]">{texto(d.hardwareInfo_model)}</span> },
      {
        id: 'bateria',
        rotulo: 'Bateria',
        largura: '96px',
        render: (d) => {
          if (d.bateria === null || d.bateria === undefined || d.bateria === '') return <span className="text-[12px] text-faint">—</span>;
          const b = numero(d.bateria);
          return <Num className={b <= 20 ? 'text-[12px] font-bold text-danger' : 'text-[12px] text-muted-foreground'}>{b}%</Num>;
        },
      },
      { id: 'hardwareInfo_serialNumber', rotulo: 'Serial Number', largura: '150px', mono: true, render: (d) => <Num className="block truncate text-[11px] text-muted-foreground">{texto(d.hardwareInfo_serialNumber)}</Num> },
      { id: 'softwareInfo_androidVersion', rotulo: 'OS', largura: '80px', render: (d) => <Num className="text-[12px] text-muted-foreground">{texto(d.softwareInfo_androidVersion)}</Num> },
      { id: 'sinalTelefone', rotulo: 'Sinal/Telefone', largura: '120px', render: (d) => <span className="text-[12px] text-muted-foreground">{texto(d.sinalTelefone)}</span> },
      { id: 'sinal_dados_moveis', rotulo: 'Sinal/Dados', largura: '110px', render: (d) => <span className="text-[12px] text-muted-foreground">{texto(d.sinal_dados_moveis)}</span> },
      /* WIFI mostrava o texto CRU que vem do aparelho - "true" / "false" -, que e
         valor de programador e nao de tela. Agora vira Sim/Não com bolinha, no
         mesmo padrao das outras colunas de estado.

         `booleanoDoLog` trata o campo como TEXTO de proposito: ele chega como
         string do log do dispositivo, e `Boolean("false")` e `true` - a conversao
         ingenua diria "Sim" para todo aparelho. Valor ausente continua "—", que e
         diferente de "não": um aparelho que nunca reportou nao e um aparelho sem
         wi-fi. */
      {
        id: 'wifi',
        rotulo: 'WIFI',
        largura: '110px',
        render: (d) => {
          const v = booleanoDoLog(d.wifi);
          if (v === null) return <span className="text-[12px] text-faint">—</span>;
          return (
            <span className="inline-flex items-center gap-1.5 text-[12px]">
              <span className={`h-1.5 w-1.5 flex-none rounded-full ${v ? 'bg-ok' : 'bg-line'}`} />
              <span className={v ? 'text-ink' : 'text-muted-foreground'}>{v ? 'Sim' : 'Não'}</span>
            </span>
          );
        },
        // A planilha leva o mesmo texto da tela, e nao "true"/"false".
        exportar: (d) => {
          const v = booleanoDoLog(d.wifi);
          return v === null ? '' : v ? 'Sim' : 'Não';
        },
      },
      { id: 'networkInfo_imei', rotulo: 'IMEI', largura: '150px', mono: true, render: (d) => <Num className="block truncate text-[11px] text-muted-foreground">{texto(d.networkInfo_imei)}</Num> },
      { id: 'networkInfo_wifiMacAddress', rotulo: 'MAC', largura: '140px', mono: true, render: (d) => <Num className="block truncate text-[11px] text-muted-foreground">{texto(d.networkInfo_wifiMacAddress)}</Num> },
      { id: 'cr', rotulo: 'CR', largura: '120px', render: (d) => <span className="block truncate text-[12px]" title={d.descricao_cr}>{texto(d.cr)}</span> },
      { id: 'cpf', rotulo: 'CPF', largura: '130px', mono: true, render: (d) => <Num className="text-[12px] text-muted-foreground">{texto(d.cpf)}</Num> },
      { id: 'endereco', rotulo: 'Endereço', largura: 'minmax(180px,1.6fr)', render: (d) => <span className="block truncate text-[12px] text-muted-foreground" title={d.endereco}>{texto(d.endereco)}</span> },
      { id: 'consumo_wifi', rotulo: 'Consumo/WIFI', largura: '130px', render: (d) => <Num className="text-[12px] text-muted-foreground">{texto(d.consumo_wifi)}</Num> },
      { id: 'consumo_dados_moveis', rotulo: 'Consumo/Dados', largura: '140px', render: (d) => <Num className="text-[12px] text-muted-foreground">{texto(d.consumo_dados_moveis)}</Num> },
      { id: 'networkInfo_telephonyInfos_phoneNumber_1', rotulo: 'Telefone 1', largura: '130px', mono: true, render: (d) => <Num className="block truncate text-[12px] text-muted-foreground">{texto(d.networkInfo_telephonyInfos_phoneNumber_1)}</Num> },
      { id: 'networkInfo_telephonyInfos_phoneNumber_2', rotulo: 'Telefone 2', largura: '130px', mono: true, render: (d) => <Num className="block truncate text-[12px] text-muted-foreground">{texto(d.networkInfo_telephonyInfos_phoneNumber_2)}</Num> },
      { id: 'networkInfo_telephonyInfos_carrierName_1', rotulo: 'Operadora 1', largura: '120px', render: (d) => <span className="block truncate text-[12px] text-muted-foreground">{texto(d.networkInfo_telephonyInfos_carrierName_1)}</span> },
      { id: 'networkInfo_telephonyInfos_carrierName_2', rotulo: 'Operadora 2', largura: '120px', render: (d) => <span className="block truncate text-[12px] text-muted-foreground">{texto(d.networkInfo_telephonyInfos_carrierName_2)}</span> },
      { id: 'memoryInfo_totalRam', rotulo: 'Memória RAM', largura: '130px', render: (d) => <Num className="text-[12px] text-muted-foreground">{texto(d.memoryInfo_totalRam)}</Num> },
      { id: 'external_storage_detected', rotulo: 'Armazenamento', largura: '140px', render: (d) => <span className="block truncate text-[12px] text-muted-foreground">{texto(d.external_storage_detected)}</span> },
      { id: 'versaoHub', rotulo: 'Versão do hub', largura: '120px', render: (d) => <Num className="text-[12px] text-muted-foreground">{texto(d.versaoHub)}</Num> },
      {
        id: 'status',
        rotulo: 'Status',
        largura: '110px',
        ordenavel: true,
        // Na planilha vai o ROTULO (ATIVO/INATIVO/PERDIDO), nao o codigo numerico:
        // quem abre o arquivo precisa filtrar por palavra, nao decorar que 1 e ativo.
        exportar: (d) => rotuloStatus(d).rotulo,
        render: (d) => {
          const s = rotuloStatus(d);
          return (
            <span className={`inline-flex h-[22px] items-center gap-1.5 rounded-md px-2 text-[11px] font-bold tracking-wide ${s.classe}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {s.rotulo}
            </span>
          );
        },
      },
    ],
    []
  );

  const colunas = useMemo(() => todasColunas.filter((c) => !ocultas.includes(c.id)), [todasColunas, ocultas]);

  // A coluna de opcoes e FIXA na primeira posicao e nao entra no seletor - e o
  // acesso as sete acoes, nao um dado que se esconde.
  const grade = useMemo(() => `56px ${colunas.map((c) => c.largura).join(' ')}`, [colunas]);
  const larguraMinima = 56 + colunas.length * 130;

  const totalPaginas = Math.max(1, Math.ceil(total / linhasPorPagina));
  const base = indices.total || total;

  const ACOES: Array<{ rotulo: string; ao: (d: Dispositivo) => void; perigo?: boolean }> = [
    // A acao do menu segue navegando na mesma aba; quem quer outra aba usa o
    // Ctrl+clique no DEVICE_ID da linha, que agora e link.
    { rotulo: 'Ver detalhe', ao: (d) => irParaDetalhe(d) },
    { rotulo: 'Editar dispositivo', ao: (d) => abrir(setOpenCR, d) },
    { rotulo: 'Geolocalização', ao: (d) => abrir(setOpenGrafico, d) },
    { rotulo: 'Mudar a senha', ao: (d) => abrir(setOpenSenha, d) },
    { rotulo: 'Histórico', ao: (d) => abrir(setOpenHistorico, d) },
    { rotulo: 'Mudar grupo', ao: (d) => abrir(setOpenGrupo, d) },
    { rotulo: 'Perda/Roubo', ao: (d) => abrir(setOpenPerda, d) },
    {
      rotulo: 'Apagar',
      perigo: true,
      ao: (d) => {
        setMenuAberto(null);
        setApagando(d);
      },
    },
  ];

  return (
    <Page title="Dispositivos">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-1.5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <TituloTela
              descricao={
                <>
Status <em>ATIVO</em> descreve o cadastro, não o aparelho — quem responde por isso é a{' '}
              <strong>última sincronização</strong>, que é a ordenação padrão da lista.
                </>
              }
            >
              Dispositivos
            </TituloTela>
          </div>

        </div>

        {/* FAIXA UNICA, e nao tres cartoes soltos - 26/08/2026.

            Em HD com escala de 125% do Windows (~1093px CSS) tres cartoes com
            moldura e espaco entre eles nao caberam lado a lado: o terceiro era
            cortado, e ao permitir a quebra ele passou a ocupar uma linha inteira
            sozinho. Uma moldura so devolve as duas bordas e os dois espacos que
            faltavam, e os tres cabem.

            Vale tambem pela leitura: total, ativos e perdidos sao a MESMA
            grandeza, e numeros que se comparam pertencem a mesma moldura.

            A grade e `grid-cols-3` desde `sm` de proposito: aqui sao sempre tres,
            e quebrar em duas colunas e o que causou o problema. */}
        <div className="mb-1.5 overflow-hidden rounded-lg border border-line bg-surface shadow-card">
        <div className="grid divide-y divide-line sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {[
            { rotulo: 'Total', valor: base, legenda: 'no escopo', cor: 'text-info' },
            { rotulo: 'Ativos', valor: indices.ativos, legenda: 'cadastro ativo', cor: 'text-ok' },
            /* PERDIDOS no lugar de Inativos - pedido de 26/08/2026.

               Faz sentido: aparelho perdido e o numero que gera TRABALHO (alguem
               precisa bloquear, cobrar, dar baixa), enquanto inativo e so cadastro
               desligado. O inativo nao ficou inacessivel - ele continua no filtro
               ao lado, junto com Perdido. O backend ja devolvia `perdidos` nesta
               mesma resposta, entao nao houve mudanca no servidor. */
            { rotulo: 'Perdidos', valor: indices.perdidos, legenda: 'declarados perdidos', cor: 'text-danger' },
          ].map((c) => (
            <div key={c.rotulo} className="px-3 py-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-faint">{c.rotulo}</span>
              <div className="mt-0.5 flex items-baseline gap-2">
                <Num className={`text-xl font-extrabold leading-none ${c.cor}`}>
                  {c.valor.toLocaleString('pt-BR')}
                </Num>
                <span className="block truncate text-xs text-muted-foreground">{c.legenda}</span>
              </div>
              {base > 0 && (
                <div className="mt-1.5 h-[2px] overflow-hidden rounded-full bg-soft">
                  <div
                    className={`h-full rounded-full bg-current ${c.cor}`}
                    style={{ width: `${Math.min(100, (c.valor / base) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        </div>

        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          {/* So pesquisa no Enter ou na lupa (21/10/2026). Antes cada tecla mudava
              `busca` e disparava uma consulta na lista de 32 mil aparelhos. */}
          <CaixaBusca
            valor={busca}
            aoBuscar={(v) => {
              setPagina(0);
              setBusca(v);
            }}
            placeholder={campoBusca === 'auto' ? 'Nome, Device ID, IMEI, CPF ou serial' : 'Buscar'}
            className="w-full max-w-[300px]"
          />

          <select
            value={campoBusca}
            onChange={(e) => {
              setPagina(0);
              setCampoBusca(e.target.value);
            }}
            className="h-9 rounded-lg border border-line bg-surface px-2 text-[12px] font-semibold text-muted-foreground outline-none focus:border-primary"
          >
            {CAMPOS_BUSCA.map((c) => (
              <option key={c.valor} value={c.valor}>
                {c.rotulo}
              </option>
            ))}
          </select>

          <div className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface-2 p-1">
            {STATUS.map((o) => (
              <button
                key={o.valor}
                type="button"
                onClick={() => {
                  setPagina(0);
                  setStatus(o.valor);
                }}
                className={`inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[12px] font-semibold ${
                  status === o.valor ? 'bg-surface text-ink shadow-card' : 'text-muted-foreground hover:text-ink'
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${status === o.valor ? 'bg-current' : 'bg-line'}`} />
                {o.rotulo}
              </button>
            ))}
          </div>

          {/* EXCEL E COLUNAS DESCERAM do cabecalho para esta barra (27/08/2026,
              a pedido: em HD eles disputavam a linha do titulo).

              Faz sentido para alem do espaco - os dois agem sobre a LISTA:
              exportar o que esta na tela e escolher quais colunas aparecem. Ao
              lado dos filtros eles ficam junto do que definem, e a linha do
              titulo volta a ter so o titulo. `ml-auto` joga os dois para a
              direita, separados dos filtros que ficam a esquerda. */}
          <div className="ml-auto flex flex-none items-center gap-2">
            {/* Exporta as COLUNAS VISIVEIS, na ordem da tela: quem escondeu 15 das
                24 colunas para conseguir ler a lista nao quer as 24 de volta na
                planilha. O rotulo do cabecalho vem da propria coluna, entao
                arquivo e tela nao divergem quando um rotulo mudar. */}
            <BotaoExportar
              nomeArquivo={`dispositivos-${id === '-1' ? 'todos' : id}`}
              linhas={() =>
                linhas.map((d) => {
                  const registro: Record<string, any> = {};
                  colunas.forEach((c) => {
                    const v = c.exportar ? c.exportar(d) : (d as any)[c.id];
                    registro[c.rotulo] = v === null || v === undefined ? '' : v;
                  });
                  return registro;
                })
              }
            />

            <div className="relative">
              <button
                type="button"
                onClick={() => setSeletorAberto((v) => !v)}
                className="h-9 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink"
              >
                Colunas ({colunas.length}/{todasColunas.length})
              </button>

              {seletorAberto && (
                <div className="absolute right-0 z-30 mt-1 max-h-[420px] w-[260px] overflow-y-auto rounded-lg border border-line bg-surface p-2 shadow-card">
                  <div className="flex items-center justify-between px-1 pb-2">
                    <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-faint">Exibir</span>
                    <button
                      type="button"
                      onClick={() => setOcultas([])}
                      className="text-[11px] font-semibold text-primary"
                    >
                      todas
                    </button>
                  </div>
                  {todasColunas.map((c) => (
                    <label
                      key={c.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-[12px] hover:bg-surface-2"
                    >
                      <input
                        type="checkbox"
                        checked={!ocultas.includes(c.id)}
                        onChange={(e) =>
                          setOcultas((v) => (e.target.checked ? v.filter((x) => x !== c.id) : [...v, c.id]))
                        }
                        className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
                      />
                      {c.rotulo}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
          <div className="overflow-x-auto">
            <div style={{ minWidth: larguraMinima }}>
              {/* CABECALHO na cor primary (fraca) - a mesma faixa do PainelLista.
                  Esta tela tem tabela PROPRIA, e nao a do PainelLista, entao os
                  dois ajustes abaixo precisavam ser feitos aqui tambem: e por isso
                  que o print do usuario continuava mostrando o defeito ja corrigido
                  no outro lugar.

                  🔴 DEFEITO CORRIGIDO - TITULOS SE SOBREPONDO ("ÚLTIMA
                  SINCRONIZAÇÃO" invadindo "NOME"): a celula tinha `min-w-0` (pode
                  encolher) mas nao `overflow-hidden`, e o `truncate` estava no
                  <button>, que e `inline-flex`. Num flex o filho nao encolhe abaixo
                  do proprio min-content sem `min-w-0` proprio - o texto vazava para
                  fora da coluna. Agora: `overflow-hidden` na celula e o rotulo num
                  <span className="min-w-0 truncate"> dentro do botao.

                  O `title` passa a levar o rotulo COMPLETO (antes era so "Ordenar
                  por esta coluna"): se o titulo pode ser cortado, o texto inteiro
                  tem que continuar alcancavel em algum lugar. */}
              <div
                className={CABECALHO_TABELA}
                style={{ gridTemplateColumns: grade }}
              >
                <div className="text-center">Opções</div>
                {colunas.map((c) => (
                  // `px-2` nas duas bordas, igual ao PainelLista: `pr-2` sozinho
                  // nao separa a coluna alinhada a direita da vizinha (o encontro
                  // acontece no limite entre as duas).
                  <div key={c.id} className="min-w-0 overflow-hidden px-2">
                    {c.ordenavel ? (
                      <button
                        type="button"
                        onClick={() => trocarOrdem(c.id)}
                        // Mesma correcao do PainelLista: `text-ink` (quase preto) sobre
                        // a faixa escura do cabecalho deixava a coluna ordenada
                        // ilegivel. Destaque por sublinhado, e o inativo levemente
                        // rebaixado.
                        className={`inline-flex w-full items-center gap-1 overflow-hidden uppercase tracking-[0.07em] hover:underline ${
                          ordenar === c.id ? 'underline underline-offset-2' : 'opacity-80 hover:opacity-100'
                        }`}
                        title={`${c.rotulo} — ordenar por esta coluna`}
                      >
                        <span className="min-w-0 truncate">{c.rotulo}</span>
                        {ordenar === c.id && <span className="flex-none">{direcao === 'asc' ? '▲' : '▼'}</span>}
                      </button>
                    ) : (
                      // Sem botao quando o backend nao ordena por esta coluna:
                      // cabecalho clicavel que nao ordena e pior que estatico.
                      <span className="block truncate" title={c.rotulo}>
                        {c.rotulo}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {carregando &&
                Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="grid items-center border-b px-3.5"
                    style={{ gridTemplateColumns: grade, borderColor: 'var(--gps-soft)', minHeight: 'var(--gps-row-h)' }}
                  >
                    <div />
                    {colunas.map((c) => (
                      <Skeleton key={c.id} className="h-3 w-20" />
                    ))}
                  </div>
                ))}

              {!carregando && erro && (
                <div className="p-8 text-center text-[12.5px] text-danger">
                  {erro}
                  <button
                    type="button"
                    onClick={carregar}
                    className="ml-2 font-semibold text-primary underline underline-offset-2"
                  >
                    tentar de novo
                  </button>
                </div>
              )}

              {!carregando && !erro && linhas.length === 0 && (
                <div className="p-8 text-center text-[12.5px] text-muted-foreground">
                  Nenhum dispositivo encontrado com esse filtro.
                </div>
              )}

              {!carregando &&
                !erro &&
                linhas.map((d, i) => (
                  <div
                    key={d.deviceId || `sem-device-${i}`}
                    className="grid items-center border-b px-3.5 hover:bg-surface-2"
                    style={{ gridTemplateColumns: grade, borderColor: 'var(--gps-soft)', minHeight: 'var(--gps-row-h)' }}
                  >
                    {/* O MENU ABRIA E NAO APARECIA (29/09/2026, relatado por quem usa).

                        Era um menu proprio, `position: absolute` dentro da
                        celula - e a celula vive dentro de dois contêineres com
                        recorte: `overflow-hidden` na moldura da tabela e
                        `overflow-x-auto` na faixa de rolagem. O menu montava,
                        ficava no DOM, e era cortado. Clicar nos tres pontos
                        parecia nao fazer nada.

                        `MenuAcoes` existe exatamente para isso: joga o menu em
                        `document.body` por portal, com `position: fixed` medida
                        a partir do botao, entao contêiner nenhum o recorta. As
                        outras NOVE listas do sistema ja usavam; esta tinha uma
                        segunda implementacao, e foi a que quebrou.

                        `ancorarADireita` nao: esta e a PRIMEIRA coluna. */}
                    <div className="flex justify-center">
                      {!podeAgir ? (
                        <span className="text-faint">—</span>
                      ) : (
                        <MenuAcoes
                          linha={d}
                          acoes={ACOES}
                          aberto={menuAberto === d.deviceId}
                          onAlternar={() => setMenuAberto(menuAberto === d.deviceId ? null : d.deviceId || null)}
                          onFechar={() => setMenuAberto(null)}
                        />
                      )}
                    </div>

                    {colunas.map((c) => (
                      // `px-2`, o mesmo recuo do cabecalho - senao o titulo deixa de
                      // ficar em cima do dado que ele nomeia.
                      <div key={c.id} className="min-w-0 px-2">
                        {c.render(d)}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3.5 py-2.5 pr-16 text-[12px] text-muted-foreground">
            <span>
              Mostrando <Num>{linhas.length}</Num> de <Num>{total.toLocaleString('pt-BR')}</Num> dispositivos
            </span>

            {/* SELETOR DE LINHAS no rodape, colado no contador.

                E ali que a pessoa percebe que a lista esta curta ("Mostrando 5
                de 30.229"), entao e ali que a correcao tem que estar - no topo,
                junto dos filtros, ele responderia a uma pergunta que ainda nao
                foi feita. */}
            <label className="flex items-center gap-1.5">
              por página
              <select
                className="h-7 rounded-md border border-line bg-surface px-1.5 text-[11px]"
                value={linhasPorPagina}
                onChange={(e) => {
                  setLinhasPorPagina(Number(e.target.value));
                  setPagina(0);
                }}
              >
                {OPCOES_LINHAS.map((n) => (
                  <option key={n} value={n}>
                    {rotuloLinhas(n)}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={pagina === 0}
                onClick={() => setPagina((p) => Math.max(0, p - 1))}
                className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
              >
                Anterior
              </button>
              <span>
                <Num>{pagina + 1}</Num> / <Num>{totalPaginas}</Num>
              </span>
              <button
                type="button"
                disabled={pagina + 1 >= totalPaginas}
                onClick={() => setPagina((p) => p + 1)}
                className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmacao de APAGAR no padrao novo. Antes era o SweetAlert2; o texto
          agora diz QUAL aparelho, porque "tem certeza?" sem o identificador e
          onde se apaga o dispositivo errado. */}
      <Modal
        aberto={Boolean(apagando)}
        titulo="Apagar dispositivo?"
        descricao="O aparelho sai do inventário e precisa ser reprovisionado para voltar."
        icone="!"
        largura="max-w-[480px]"
        onFechar={() => setApagando(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setApagando(null)} disabled={removendo}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={apagar}
              disabled={removendo}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {removendo ? 'Apagando...' : 'Apagar dispositivo'}
            </button>
          </>
        }
      >
        {apagando && (
          <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
            <div className="font-semibold">{texto(apagando.nome)}</div>
            <Num className="block text-[11px] text-muted-foreground">{texto(apagando.deviceId)}</Num>
            <div className="mt-2 text-muted-foreground">
              Grupo: <strong className="text-ink">{texto(apagando.grupoNome)}</strong>
            </div>
          </div>
        )}
      </Modal>

      {/* Os seis modais da tela antiga, com os mesmos contratos de prop. */}
      <ModalEditarDispositivo
        aberto={Boolean(selecionado?.deviceId) && openCR}
        device={selecionado}
        onFechar={() => setOpenCR(false)}
        onSalvo={carregar}
      />
      <ModalMudarGrupoNovo
        aberto={Boolean(selecionado?.deviceId) && openGrupo}
        device={selecionado}
        onFechar={() => setOpenGrupo(false)}
        onSalvo={carregar}
      />
      <ModalHistoricoDispositivo
        aberto={Boolean(selecionado?.deviceId) && openHistorico}
        device={selecionado}
        onFechar={() => setOpenHistorico(false)}
      />
      <ModalMudarSenha
        aberto={Boolean(selecionado?.deviceId) && openSenha}
        device={selecionado}
        onFechar={() => setOpenSenha(false)}
        onSalvo={carregar}
      />
      <ModalPerdaRoubo
        aberto={Boolean(selecionado?.deviceId) && openPerda}
        device={selecionado}
        onFechar={() => setOpenPerda(false)}
        onSalvo={carregar}
      />
      <ModalGeolocalizacao
        aberto={Boolean(selecionado?.deviceId) && openGrafico}
        device={selecionado}
        onFechar={() => setOpenGrafico(false)}
      />
    </Page>
  );
}
